"use client";

import { useEffect, useState } from "react";
import { Toaster, ToastPosition } from "react-hot-toast";
import styles from "./toast.module.scss";

/**
 * A React functional component that renders a `Toaster` with dynamic positioning
 * based on the window's width. The position of the toast updates responsively
 * when the window is resized.
 *
 * @returns {JSX.Element} The `Toaster` component with configurable options.
 *
 * @remarks
 * - The toast position switches between "bottom-right" and "top-center" based on
 *   the screen width relative to a breakpoint defined in `styles.smallBreakPoint`.
 * - The `Toaster` is styled with a container style that applies consistent spacing
 *   on all sides.
 *
 * @internal
 * The `styles.smallBreakPoint` value must be defined and accessible for the
 * component to function correctly.
 */
export const ToasterContainer = () => {
  const [toastPosition, setToastPosition] =
    useState<ToastPosition>("bottom-right");

  useEffect(() => {
    const updateToastPosition = () => {
      // Calculate when toast should change position
      if (window.innerWidth <= parseInt(styles.smallBreakPoint)) {
        setToastPosition("top-center");
      } else {
        setToastPosition("bottom-right");
      }
    };

    updateToastPosition();

    window.addEventListener("resize", updateToastPosition);

    return () => {
      window.removeEventListener("resize", updateToastPosition);
    };
  }, []);

  return (
    <Toaster
      toastOptions={{
        duration: 2000,
      }}
      position={toastPosition}
      containerStyle={{ right: 40, bottom: 40, left: 40, top: 40 }}
    />
  );
};

export default ToasterContainer;
